import { Card, Chip, faDigits, formatDate, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  api,
  type ShortageForecastRow,
  type SiteRow,
  type StockItemRow,
  type WarehouseRow,
} from "../../api.js";
import { addDays, tehranToday } from "../../labels.js";
import { formatQty, formatRial } from "./format.js";

export function ForecastTab() {
  const [date, setDate] = useState(() => addDays(tehranToday(), 1));
  const [kitchenId, setKitchenId] = useState<string>("");

  const sites = useQuery({
    queryKey: ["sites"],
    queryFn: () => api<SiteRow[]>("/sites"),
  });

  const warehouses = useQuery({
    queryKey: ["inventory", "warehouses"],
    queryFn: () => api<WarehouseRow[]>("/inventory/warehouses"),
  });

  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });

  const kitchens = (sites.data ?? []).filter((s) => s.kind === "kitchen");
  const currentKitchenId = kitchenId || kitchens[0]?.id || "";

  const forecast = useQuery({
    queryKey: ["inventory", "forecast", date, currentKitchenId],
    queryFn: () =>
      api<ShortageForecastRow>(`/inventory/forecast/shortages?date=${date}&kitchenId=${currentKitchenId}`),
    enabled: Boolean(currentKitchenId && date),
  });

  const itemsMap = new Map((items.data ?? []).map((i) => [i.id, i]));
  const whMap = new Map((warehouses.data ?? []).map((w) => [w.id, w]));

  const currentKitchen = kitchens.find((k) => k.id === currentKitchenId);
  const shortageData = forecast.data;
  const shortageList = shortageData?.items ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Date & Kitchen filter */}
      <div className="row wrap items-center" style={{ gap: "var(--s3)" }}>
        <div className="field" style={{ minWidth: "220px" }}>
          <label htmlFor="fc-kitchen">آشپزخانه</label>
          <select
            id="fc-kitchen"
            className="input"
            value={currentKitchenId}
            onChange={(e) => {
              setKitchenId(e.target.value);
            }}
          >
            {kitchens.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field" style={{ width: "auto" }}>
          <label htmlFor="fc-date">تاریخ برنامه تولید (پیش‌فرض فردا)</label>
          <input
            id="fc-date"
            type="date"
            className="input"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
            }}
          />
        </div>
      </div>

      {/* Summary Cards */}
      {shortageData && (
        <div className="row wrap" style={{ gap: "var(--s3)" }}>
          <Card className="flex-1">
            <div className="stack" style={{ gap: "var(--s1)" }}>
              <span className="hint">تعداد اقلام دارای کسری</span>
              <span className="t-card num b" style={{ fontSize: "1.4rem" }}>
                {faDigits(String(shortageList.length))} قلم
              </span>
            </div>
          </Card>

          <Card className="flex-1">
            <div className="stack" style={{ gap: "var(--s1)" }}>
              <span className="hint">برآورد کل هزینه تأمین کسری</span>
              <span
                className="t-card num b"
                style={{
                  fontSize: "1.4rem",
                  color: shortageList.length > 0 ? "var(--coral-ink)" : "var(--teal-ink)",
                }}
              >
                {formatRial(shortageData.totalEstimatedCostRial)}
              </span>
            </div>
          </Card>
        </div>
      )}

      {/* Shortage details table */}
      <Panel title={`پیش‌بینی کسری مواد اولیه: ${currentKitchen?.name ?? ""} (${formatDate(date)})`}>
        {!currentKitchenId ? (
          <p className="hint">لطفاً یک آشپزخانه را انتخاب کنید.</p>
        ) : forecast.isLoading ? (
          <p className="hint">در حال محاسبه برنامه تولید و مقایسه با موجودی انبارها...</p>
        ) : shortageList.length === 0 ? (
          <div className="stack" style={{ gap: "var(--s2)", padding: "var(--s2) 0" }}>
            <Chip tone="ok">موجودی کافی است</Chip>
            <p className="hint">
              هیچ کسری ماده اولیه‌ای برای برنامه تولید روز {formatDate(date)} در آشپزخانه{" "}
              {currentKitchen?.name} وجود ندارد.
            </p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>کد کالا</th>
                  <th>نام ماده اولیه</th>
                  <th>انبار تخصیص‌یافته</th>
                  <th>نیاز کل تولید</th>
                  <th>موجودی فعلی</th>
                  <th>کسری موردنیاز</th>
                  <th>برآورد بهای تأمین</th>
                </tr>
              </thead>
              <tbody>
                {shortageList.map((row, idx) => {
                  const item = itemsMap.get(row.itemId);
                  const wh = whMap.get(row.warehouseId);
                  return (
                    <tr key={`${row.itemId}-${String(idx)}`}>
                      <td>{item?.code ?? "—"}</td>
                      <td className="b">{item?.name ?? row.itemId}</td>
                      <td>{wh?.name ?? row.warehouseId}</td>
                      <td className="num">{formatQty(row.neededMilli, item?.baseUnit)}</td>
                      <td className="num">{formatQty(row.currentBalanceMilli, item?.baseUnit)}</td>
                      <td className="num b" style={{ color: "var(--coral-ink)" }}>
                        {formatQty(row.shortageMilli, item?.baseUnit)}
                      </td>
                      <td className="num b">{formatRial(row.estimatedCostRial)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
